import {
  type AIAgent,
  type AIConnector,
  getAgentName,
  getAgentOwnerEmail,
  getConnectorDisplayName,
} from "@p0security/shared/types/ai-inventory";
import { useMemo } from "react";

import { SidecarGraph } from "../graph/SidecarGraph.tsx";
import type { AgentGraphData } from "../graph/types.ts";
import type { ManagedMcpServer } from "../hooks/useManagedMcpServers.ts";
import { isConnectorManaged } from "../platforms/enrollment.ts";
import { SidecarHeader, type SidecarHeaderStats } from "./SidecarHeader.tsx";

export type AgentSidecarProps = {
  agent: AIAgent | undefined;
  connectors: AIConnector[];
  graphData: AgentGraphData | undefined;
  onClose: () => void;
  onConnectorSelect: (connector: AIConnector) => void;
  onServerSelect?: (server: ManagedMcpServer) => void;
  onSecureConnector?: (connector: AIConnector) => void;
  /** True while traffic-derived data (e.g. local-agents observed servers) is still loading. */
  graphLoading?: boolean;
};

export const AgentSidecar: React.FC<AgentSidecarProps> = ({
  agent,
  connectors,
  graphData,
  onClose,
  onConnectorSelect,
  onServerSelect,
  onSecureConnector,
  graphLoading,
}) => {
  const stats = useMemo<SidecarHeaderStats | null>(() => {
    if (!agent || !graphData) return null;
    const ownerEmail = getAgentOwnerEmail(agent);
    const allRisks = [
      ...graphData.connectors.flatMap((c) => c.risks),
      ...Object.values(graphData.serverRisks).flat(),
    ];
    const connectorList = connectors.map((c) => ({
      name: getConnectorDisplayName(c),
      managed: isConnectorManaged(c),
    }));
    const enforcingCount = graphData.p0Mcp?.enforcingCount ?? 0;
    const connectorStats: SidecarHeaderStats["connectors"] =
      agent.platform === "local-agents"
        ? { kind: "servers", servers: enforcingCount }
        : {
            kind: "connectors",
            count: connectors.length,
            servers: enforcingCount,
          };
    return {
      agentName: getAgentName(agent),
      status: ownerEmail ? "Enrolled" : "Unenrolled",
      ownerEmail,
      connectors: connectorStats,
      connectorList,
      risks: {
        high: allRisks.filter((r) => r.severity === "high").length,
        total: allRisks.length,
      },
      policies: {
        enabled: graphData.p0Mcp?.policies.filter((p) => p.enabled).length ?? 0,
        total: graphData.p0Mcp?.policies.length ?? 0,
      },
      policyList: graphData.p0Mcp?.policies.map((p) => ({
        id: p.id,
        name: p.name,
        enabled: p.enabled,
      })),
    };
  }, [agent, connectors, graphData]);

  if (!agent || !stats || !graphData) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        height: "100%",
      }}
    >
      <SidecarHeader stats={stats} onClose={onClose} />
      <SidecarGraph
        data={graphData}
        onConnectorClick={onConnectorSelect}
        onServerClick={onServerSelect}
        onSecureConnector={onSecureConnector}
        fillHeight
        loading={graphLoading}
      />
    </div>
  );
};
